/**
 * CashflowHeader - Navigation header with month selector and AI input trigger
 */

import { useState } from 'react';
import { format } from 'date-fns';
import { tr } from 'date-fns/locale';
import { motion, AnimatePresence } from 'framer-motion';
import { useCashflowStore } from '../../stores/cashflowStore';
import {
  ChevronLeft,
  ChevronRight,
  Sparkles,
  Calendar,
  Home,
  Plus,
  FileUp,
  ListOrdered,
  Brain,
  CreditCard,
  PieChart,
  List,
  ToggleLeft,
  ToggleRight,
} from 'lucide-react';
import { Link } from 'react-router-dom';
import CategoryFilter from './CategoryFilter';
import PaymentPlanModal from './PaymentPlanModal';
import FinancialAnalysisModal from './FinancialAnalysisModal';

const CashflowHeader = () => {
  const [isPaymentPlanOpen, setIsPaymentPlanOpen] = useState(false);
  const [isAnalysisOpen, setIsAnalysisOpen] = useState(false);
  const {
    currentMonth,
    goToPreviousMonth,
    goToNextMonth,
    goToToday,
    openAIModal,
    openEditModal,
    openScheduleModal,
    openCreditCardModal,
    openCreditCardSidebar,
    openSpendingAnalysis,
    creditCardStatements,
    creditCardPaymentMode,
    toggleCreditCardPaymentMode,
  } = useCashflowStore();

  const monthYear = format(currentMonth, 'MMMM yyyy', { locale: tr });

  return (
    <header className="bg-gray-900/80 backdrop-blur-sm border-b border-gray-800 sticky top-0 z-40">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16">
          {/* Left: Navigation */}
          <div className="flex items-center gap-4">
            <Link
              to="/"
              className="p-2 text-gray-400 hover:text-white hover:bg-gray-800 rounded-lg transition-colors"
              title="Ana Sayfa"
            >
              <Home className="w-5 h-5" />
            </Link>

            <div className="h-6 w-px bg-gray-700" />

            <h1 className="text-xl font-semibold text-white flex items-center gap-2">
              <Calendar className="w-5 h-5 text-indigo-400" />
              Cashflow
            </h1>
          </div>

          {/* Center: Month Navigation */}
          <div className="flex items-center gap-2">
            <button
              onClick={goToPreviousMonth}
              className="p-2 text-gray-400 hover:text-white hover:bg-gray-800 rounded-lg transition-colors"
              title="Önceki ay"
            >
              <ChevronLeft className="w-5 h-5" />
            </button>

            <motion.button
              onClick={goToToday}
              className="px-4 py-2 text-white font-medium hover:bg-gray-800 rounded-lg transition-colors min-w-[180px] text-center capitalize"
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
            >
              {monthYear}
            </motion.button>

            <button
              onClick={goToNextMonth}
              className="p-2 text-gray-400 hover:text-white hover:bg-gray-800 rounded-lg transition-colors"
              title="Sonraki ay"
            >
              <ChevronRight className="w-5 h-5" />
            </button>
          </div>

          {/* Right: Actions */}
          <div className="flex items-center gap-2">
            {/* Category Filter */}
            <CategoryFilter />

            {/* AI Analysis button */}
            <motion.button
              onClick={() => setIsAnalysisOpen(true)}
              className="flex items-center gap-2 px-3 py-2 text-purple-400 hover:text-purple-300 hover:bg-purple-500/10 rounded-lg transition-colors"
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              title="AI Finansal Analiz"
            >
              <Brain className="w-4 h-4" />
              <span className="hidden sm:inline">Analiz</span>
            </motion.button>

            {/* Credit Card Statement buttons */}
            <div className="flex items-center">
              <motion.button
                onClick={openCreditCardModal}
                className="flex items-center gap-2 px-3 py-2 text-pink-400 hover:text-pink-300 hover:bg-pink-500/10 rounded-l-lg transition-colors"
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                title="Ekstre Yükle"
              >
                <CreditCard className="w-4 h-4" />
                <span className="hidden sm:inline">Ekstre</span>
              </motion.button>
              <motion.button
                onClick={openCreditCardSidebar}
                className="flex items-center gap-1 px-2 py-2 text-pink-400 hover:text-pink-300 hover:bg-pink-500/10 rounded-r-lg border-l border-pink-500/20 transition-colors"
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                title="Ekstreleri Görüntüle"
              >
                <List className="w-4 h-4" />
                {creditCardStatements.length > 0 && (
                  <span className="text-xs bg-pink-500/30 px-1.5 py-0.5 rounded-full">
                    {creditCardStatements.length}
                  </span>
                )}
              </motion.button>
            </div>

            {/* Credit Card Payment Mode Toggle */}
            {creditCardStatements.length > 0 && (
              <motion.button
                onClick={toggleCreditCardPaymentMode}
                className={`flex items-center gap-1.5 px-2 py-1.5 rounded-lg text-xs font-medium transition-all ${
                  creditCardPaymentMode === 'minimum'
                    ? 'bg-green-500/20 text-green-400 border border-green-500/30'
                    : 'bg-orange-500/20 text-orange-400 border border-orange-500/30'
                }`}
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                title={creditCardPaymentMode === 'minimum'
                  ? 'Asgari ödeme gösteriliyor - Tam borç için tıklayın'
                  : 'Tam borç gösteriliyor - Asgari ödeme için tıklayın'
                }
              >
                {creditCardPaymentMode === 'minimum' ? (
                  <>
                    <ToggleLeft className="w-4 h-4" />
                    <span className="hidden lg:inline">Asgari</span>
                  </>
                ) : (
                  <>
                    <ToggleRight className="w-4 h-4" />
                    <span className="hidden lg:inline">Tam Borç</span>
                  </>
                )}
              </motion.button>
            )}

            {/* Spending Analysis button */}
            <motion.button
              onClick={openSpendingAnalysis}
              className="flex items-center gap-2 px-3 py-2 text-cyan-400 hover:text-cyan-300 hover:bg-cyan-500/10 rounded-lg transition-colors"
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              title="Harcama Analizi"
            >
              <PieChart className="w-4 h-4" />
              <span className="hidden sm:inline">Harcama</span>
            </motion.button>

            {/* Payment Plan button */}
            <motion.button
              onClick={() => setIsPaymentPlanOpen(true)}
              className="flex items-center gap-2 px-3 py-2 text-gray-400 hover:text-white hover:bg-gray-800 rounded-lg transition-colors"
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              title="Ödeme Planı"
            >
              <ListOrdered className="w-4 h-4" />
              <span className="hidden sm:inline">Plan</span>
            </motion.button>

            {/* Manual add button */}
            <motion.button
              onClick={() => openEditModal(null)}
              className="flex items-center gap-2 px-3 py-2 text-gray-400 hover:text-white hover:bg-gray-800 rounded-lg transition-colors"
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              title="Manuel ekle"
            >
              <Plus className="w-4 h-4" />
              <span className="hidden sm:inline">Ekle</span>
            </motion.button>

            {/* Import from file button */}
            <motion.button
              onClick={openScheduleModal}
              className="flex items-center gap-2 px-3 py-2 text-gray-400 hover:text-white hover:bg-gray-800 rounded-lg transition-colors"
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              title="PDF/Excel'den içe aktar"
            >
              <FileUp className="w-4 h-4" />
              <span className="hidden sm:inline">İçe Aktar</span>
            </motion.button>

            {/* AI Input button */}
            <motion.button
              onClick={openAIModal}
              className="flex items-center gap-2 px-4 py-2 bg-indigo-600 hover:bg-indigo-500 text-white rounded-lg transition-colors shadow-lg shadow-indigo-500/20"
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
            >
              <Sparkles className="w-4 h-4" />
              <span className="hidden sm:inline">AI ile Ekle</span>
            </motion.button>
          </div>
        </div>
      </div>

      {/* Payment Plan Modal */}
      <AnimatePresence>
        {isPaymentPlanOpen && (
          <PaymentPlanModal
            isOpen={isPaymentPlanOpen}
            onClose={() => setIsPaymentPlanOpen(false)}
          />
        )}
      </AnimatePresence>

      {/* Financial Analysis Modal */}
      <AnimatePresence>
        {isAnalysisOpen && (
          <FinancialAnalysisModal
            isOpen={isAnalysisOpen}
            onClose={() => setIsAnalysisOpen(false)}
          />
        )}
      </AnimatePresence>
    </header>
  );
};

export default CashflowHeader;
